import { HttpErrorResponse, HttpEvent, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestMethod } from '@api/abstracts/models';
import { environment } from '@environments/environment';
import { mock } from '@mock/mock';
import { Mock } from '@mock/models';
import { normalizeUrlPath } from '@shared/utils';
import { delay, Observable, of, throwError } from 'rxjs';

/**
 * Service simulating HTTP responses with predefined mock configurations for testing and development.
 *
 * @remarks
 * - Enabled/disabled via `environment.isMockEnabled`
 * - Matches requests by HTTP method and URL
 * - Supports response delays and custom status codes
 *
 * @example
 * ```
 * const mockData = inject(MockData);
 * mockData.getMockResponse('GET', '/api/users')?.subscribe({
 *   next: (response) => console.log(response),
 *   error: (err) => console.error(err)
 * });
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class MockData {
  isActivated: boolean = environment.isMockEnabled;

  /**
   * Generates a mock HTTP response matching the request method and URL.
   *
   * @param method - HTTP request method
   * @param url - Fully qualified URL
   * @returns Observable emitting mock HttpEvent, or undefined if no matching schema found
   *
   * @remarks
   * - Status codes 4xx-5xx return HttpErrorResponse
   * - Supports optional response delay from schema configuration
   *
   * @example
   * ```
   * const response$ = mockData.getMockResponse('GET', '/api/data');
   * if (response$) {
   *   response$.subscribe((res) => console.log(res));
   * }
   * ```
   */
  getMockResponse(method: keyof typeof RequestMethod, url: string): Observable<HttpEvent<unknown>> | undefined {
    const schema: Mock | undefined = this.findSchema(method, url);
    if (!schema) return undefined;

    const { status = 200, schema: body, delay: mockDelay = 0 } = schema;
    const isError = status >= 400 && status < 600;

    const response$ = isError
      ? throwError(
          () =>
            new HttpErrorResponse({
              error: 'Error simulated by the mock.',
              status,
              statusText: 'Error simulated by the mock.',
            })
        )
      : of(
          new HttpResponse({
            status,
            body,
          })
        );

    return mockDelay > 0 ? response$.pipe(delay(mockDelay)) : response$;
  }

  /**
   * Finds a matching mock schema by HTTP method and URL.
   *
   * @param method - HTTP request method
   * @param url - Request URL
   * @returns Matching mock schema or undefined
   */
  private findSchema(method: keyof typeof RequestMethod, url: string): Mock | undefined {
    return mock.find((schema) => {
      const endpoint = environment.API.endpoint[schema.apiEndpoint ?? 'default'];
      const endpointSchema = normalizeUrlPath(`${endpoint}/${schema.url}`, { preserveTrailing: false });
      const isMatchingMethod = method === 'GET' ? !schema.method || schema.method === method : schema.method === method;
      const isMatchingURL = schema.useStartsWith ? url.startsWith(endpointSchema) : endpointSchema === url;
      return isMatchingMethod && isMatchingURL;
    });
  }
}
